import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {PearlHarbor} from "../PearlHarbor";
import {PearlHarborSymbol, SYMBOL_NAME} from "../reels/PearlHarborSymbol";
import {TimelineLite, TimelineMax, TweenMax} from "gsap";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {PearlHarborGameMode} from "../server/PearlHarborGameMode";
import {PearlHarborReelView} from "../reels/PearlHarborReelView";
import {PearlHarborGameConfig} from "../PearlHarborGameConfig";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {IWinRatioConfig} from "../configs/PearlHarborWinConfigs";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BitMapMultiplier} from "../view/freespin/component/BitMapMultiplier";
import {PearlHarborGameAssets} from "../PearlHarborGameAssets";

export class PearlHarborHelper {
    public static isGameStarted: boolean = false;

    public static get isIpad(): boolean {
        const resizeData: ScreenBounds = NolimitApplication.screenBounds;
        if (NolimitApplication.isLandscape) {
            return (resizeData.width >= 955 && resizeData.width <= 965);
        } else {
            return (resizeData.height >= 955 && resizeData.height <= 965);
        }
    }

    public static isFreeSpin(mode: string): boolean {
        let isFS: boolean = true;
        if (mode == PearlHarborGameMode.NORMAL || mode == PearlHarborGameMode.NORMAL_AVALANCHE) {
            isFS = false;
        }
        return isFS;
    }

    public static isAvalanche(mode: string): boolean {
        return (mode.indexOf("AVALANCHE") > -1);
    }

    public static isNotSplitSymbol(symName: string): boolean {
        return ["TARGET", SYMBOL_NAME.TARGET_SPENT, SYMBOL_NAME.SCATTER, SYMBOL_NAME.BOMB].indexOf(symName) > -1;
    }

    public static isSpecialSymbol(symbolName: string): boolean {
        return [SYMBOL_NAME.XS, "X_SPLIT_SPENT", "X_SPLIT_H", SYMBOL_NAME.TARGET, SYMBOL_NAME.TARGET_SPENT, SYMBOL_NAME.BOMB, SYMBOL_NAME.SCATTER].indexOf(symbolName) > -1;
    }

    private isSpecialSymbol(symName: string): boolean {
        return [SYMBOL_NAME.XS, "X_SPLIT_SPENT", "X_SPLIT_H", SYMBOL_NAME.TARGET, SYMBOL_NAME.BOMB].indexOf(symName) > -1;
    }

    public static isHalfSymbol(symName: string): boolean {
        return symName.indexOf("_H") > -1;
    }

    public static isXS(symName: string): boolean {
        return symName.indexOf("X_SPLIT") > -1;
    }

    public static copyLocalPosition(objToSet: PIXI.DisplayObject, objToMimic: PIXI.DisplayObject): PIXI.Point {
        return objToSet.parent.toLocal(objToSet.parent.position, objToMimic);
    }

    public static getDistance(p1: PIXI.Point, p2: PIXI.Point): number {
        const xDist: number = p2.x - p1.x;
        const yDist: number = p2.y - p1.y;
        return Math.sqrt(Math.pow(xDist, 2) + Math.pow(yDist, 2));
    }

    public static getAngleInDegree(pos1: number[], pos2: number[]): number {
        let angle: number = Math.atan2(pos1[0] - pos2[0], pos1[1] - pos2[1]);
        angle = angle < 0 ? (2 * Math.PI + angle) : angle;
        const finalAngle: number = angle * (180 / Math.PI);
        return 360 - 90 - finalAngle; //pixi object use clockwise angle.
    }

    public static createSprite(sourceName: string, parent?: PIXI.Container, pos?: number[]): TimelineSprite {
        const sprite: TimelineSprite = new TimelineSprite(PearlHarbor.assetLoader.getTextures(sourceName));
        sprite.name = sourceName;
        if (pos && pos.length) {
            sprite.position.set(pos[0], pos[1]);
        }
        sprite.anchor.set(0.5);
        parent && parent.addChild(sprite);
        return sprite;
    }

    public static getRandomIntInRange(min: number, max: number, fractionDigits: number = 0): number {
        return Number((min + (max - min) * Math.random()).toFixed(fractionDigits));
    }

    public static getAbsolutePos(symbol: PearlHarborSymbol): number[] {
        return [(symbol.reelId) * PearlHarborGameConfig.symbolWidth, (symbol.reelTargetPosition + 1.5) * PearlHarborGameConfig.symbolHeight * 0.5];
    }

    public static getSymbolAbsolutePos(reelId: number, rowId: number): number[] {
        const reelViewPos: number[] = PearlHarborHelper.getReelAbsolutePos(reelId);
        return [reelViewPos[0], reelViewPos[1] + rowId * PearlHarborGameConfig.symbolHeight * 0.5];
    }

    public static getSymbolPos(reelId: number, rowId: number): number[] {
        const reelViewPos: number[] = PearlHarborHelper.getReelPos(reelId);
        return [reelViewPos[0] + PearlHarborGameConfig.symbolWidth * 0.5, reelViewPos[1] + (rowId + 1) * PearlHarborGameConfig.symbolHeight];
    }

    public static getReelAbsolutePos(reelId: number): number[] {
        return [PearlHarbor.allReelsView.x + PearlHarbor.allReelsView.mainReelModels[reelId].reelView.x,
            PearlHarbor.allReelsView.y + PearlHarbor.allReelsView.mainReelModels[reelId].reelView.y];
    }

    public static getReelPos(reelId: number): number[] {
        return [PearlHarbor.allReelsView.mainReelModels[reelId].reelView.x,
            PearlHarbor.allReelsView.mainReelModels[reelId].reelView.y];
    }

    public static getReelMidXandY(reelId: number): number[] {
        const reelView: PearlHarborReelView = PearlHarbor.allReelsView.mainReelModels[reelId].reelView;
        return [PearlHarbor.allReelsView.x + reelView.x + reelView.width * 0.5, PearlHarbor.allReelsView.y + reelView.y];
    }

    public static isActiveReel(reel: number): boolean {
        return reel >= PearlHarbor.activeSymbolsBackground.targetArea.reelStart && reel <= PearlHarbor.activeSymbolsBackground.targetArea.reelEnd;
    }

    public static isActiveCell(reel: number, row: number) {
        return this.isActiveReel(reel) && this.isActiveRow(row);
    }


    public static isActiveRow(row: number): boolean {
        return row >= PearlHarbor.activeSymbolsBackground.targetArea.rowStart && row <= PearlHarbor.activeSymbolsBackground.targetArea.rowEnd;
    }

    public static isMidSymbol(symbol: string): boolean {
        return ["M1", "M2", "M3", "M4", "M5"].indexOf(symbol.toUpperCase()) > -1;
    }

    public static isLowSymbol(symbol: string): boolean {
        return ["L1", "L2", "L3", "L4", "L5"].indexOf(symbol.toUpperCase()) > -1;
    }

    public static shakeScreen(distance: number = 2, duration: number = 0.01, repeat: number = 20): TimelineLite {
        //Logger.logDev("shake started");
        const tl: TimelineLite = new TimelineLite(),
            mainGameContainer: StageLayer | undefined = NolimitApplication.getLayerByName(PearlHarbor.mainGameZoomContainer.name);

        tl.add([
            TweenMax.fromTo(mainGameContainer?.parent || {}, duration, {y: -distance}, {
                y: distance,
                clearProps: "y",
                repeat: repeat
            }),
            TweenMax.fromTo(mainGameContainer?.parent || {}, duration, {x: -distance}, {
                x: distance,
                clearProps: "x",
                repeat: repeat
            })
        ]);
        return tl;
    }

    public static resetSpineItem(item: PIXI.spine.Spine): void {
        item.skeleton.setSlotsToSetupPose();
        item.skeleton.setToSetupPose();
        item.state.clearTracks();
        item.state.setEmptyAnimation(0, 0);
        item.updateTransform();
    }

    public static timeLineKiller(tl: TimelineMax, tlR?: TimelineMax, forceEnd: boolean = false): TimelineMax {
        if (!tl) return tlR ? tlR : tl;
        if (forceEnd) tl.totalProgress(1);
        tl.pause();
        tl.kill();
        tl.clear();
        return tlR ? tlR : tl;
    }

    public static removeDuplicateFromArray(sourceArr: number[]): number[] {
        const finalArray: number[] = [];
        sourceArr.forEach((value: number) => {
            if (finalArray.indexOf(value) == -1) {
                finalArray.push(value);
            }
        });
        return finalArray;
    }

    public static getExplodeArea(reelId: number, rowId: number): Map<number, number[]> {
        const idx: number = 1,
            explodePosMap: Map<number, number[]> = new Map(),
            prevReel: number = Math.max(reelId - idx, 0),
            nextReel: number = Math.min(reelId + idx, 6),
            prevRow: number = Math.max(rowId - idx, 0),
            nextRow: number = Math.min(rowId + idx, 13),
            rowArr: number[] = PearlHarborHelper.removeDuplicateFromArray([prevRow, rowId, nextRow]);

        explodePosMap.set(prevReel, rowArr);
        explodePosMap.set(reelId, rowArr);
        explodePosMap.set(nextReel, rowArr);
        return explodePosMap;
    }

    public static getBuyFeaturePrice(): number {
        let betLevel = PearlHarbor.apiPlugin.freeBets.hasFreeBets() ? PearlHarbor.apiPlugin.freeBets.getBet() : PearlHarbor.apiPlugin.betLevel.getLevel();
        let price: number = PearlHarbor.model.gameData?.playedBetValue || parseFloat(betLevel);
        if(PearlHarbor.model.data.wasFeatureBuy || PearlHarbor.model.buyFeatureName === "BOOSTED_BET" || PearlHarbor.model.buyFeatureName === "BOOSTED_BET_2") {
            for(let index = 0; index < PearlHarbor.model.initData.featureBuyTimesBetValue.length; index++) {
                if(PearlHarbor.model.initData.featureBuyTimesBetValue[index].name === PearlHarbor.model.buyFeatureName) {
                    price *= PearlHarbor.model.initData.featureBuyTimesBetValue[index].price;
                    break;
                }
            }
        }
        return price;
    }

    public static isWinBelowStake(): boolean {
        return PearlHarbor.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && (PearlHarbor.model.data.accumulatedRoundWin <= this.getBuyFeaturePrice());
    }

    public static isScatterSymbol(symbol: PearlHarborSymbol): boolean {
        return (symbol.symbolName === SYMBOL_NAME.SCATTER);
    }

    public static isMegaBomb(symbol: PearlHarborSymbol): boolean {
        if ((this.isScatterSymbol(symbol)) && (symbol.reelId === 3 && symbol.reelTargetPosition === 3)) {
            return true;
        } else {
            return false;
        }
    }

    public static createCoinValue(value: number, position: PIXI.Point, preFixedText?: string, postFixedText?: string, name?: string): BitMapMultiplier {
        const coinValue: BitMapMultiplier = new BitMapMultiplier(PearlHarborGameAssets.BITMAP_FONT_FS_ACTIVE, 32, preFixedText, postFixedText);
        coinValue.value = value;
        coinValue.position.set(position.x + 18, position.y - 10);
        coinValue.anchor = new PIXI.Point(0.5, 0.5);
        coinValue.alpha = 0;
        coinValue.name = name || "MULTIPLIER_" + value;
        return coinValue;
    }

    public static isBigWin(): boolean {
        const winRatioConfig: IWinRatioConfig = PearlHarbor.totalWinView.getWinRatioConfig();

        return winRatioConfig.type.indexOf("bigWin") > -1;
    }

    public static getBigWinLevel(): number {
        const winRatioConfig: IWinRatioConfig = PearlHarbor.totalWinView.getWinRatioConfig();

        let bigWinLevel: number = 0;
        if (this.isBigWin()) {
            bigWinLevel = parseInt(winRatioConfig.type.replace("bigWin", ""));
        }

        return bigWinLevel;
    }

    public static isEqualData(obj1: any, obj2: any): boolean {
        let aProps: string[] = Object.getOwnPropertyNames(obj1);
        let bProps: string[] = Object.getOwnPropertyNames(obj2);

        if (aProps.length != bProps.length) {
            return false;
        }

        for (let i: number = 0; i < aProps.length; i++) {
            let propName: string = aProps[i];
            if (obj1[propName] !== obj2[propName]) {
                return false;
            }
        }

        return true;
    }

    public static getCountUpValue(value: number): number {
        let retVal: number;
        if (value === 1) {
            retVal = 0;
        } else if (value > 1 && value < 26) {
            retVal = 5;
        } else if (value > 25 && value < 51) {
            retVal = 50;
        } else {
            retVal = 100;
        }
        return retVal;
    }
}